import React, { useEffect, useRef, useState } from "react";
import { formatDate } from "../../utils/formatDate";
import { STATUS } from "../constants/statuses";
import { checkAndAddStatusNotifications } from "../../notifications/notificationChecker";
import axiosPublic from "api/axiosConfig";
import { renderRequestTracker } from "../../components/ui/RequestTracker";
import { Clock4 } from "lucide-react";

function UserRequests({ userId, notifications, refreshTrigger }) {
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const prevNotificationsRef = useRef([]);

  const fetchRequests = async () => {
    try {
      const response = await axiosPublic.get(`/user/${userId}/requests`, {
        params: {
          status: statusFilter || undefined,
        },
      });

      const fetchedRequests = response.data;
      checkAndAddStatusNotifications(fetchedRequests, userId);

      setRequests(response.data);
    } catch (error) {
      console.error("Failed to fetch user requests:", error);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [userId, statusFilter, refreshTrigger]);

  useEffect(() => {
    if (!notifications.length) return;

    const newNotifications = notifications.filter(
      (n) => !prevNotificationsRef.current.some((prev) => prev.id === n.id),
    );

    if (
      newNotifications.some((n) =>
        [STATUS.APPROVED, STATUS.REJECTED, STATUS.PLAYED].some((status) =>
          n.message.includes(status),
        ),
      )
    ) {
      fetchRequests();
    }

    prevNotificationsRef.current = [...notifications];
  }, [notifications]);

  const tabs = {
    All: "",
    Pending: STATUS.PENDING,
    Approved: STATUS.APPROVED,
    Paid: STATUS.PAID,
    Played: STATUS.PLAYED,
    Rejected: STATUS.REJECTED,
  };

  return (
    <div className="text-white py-6 min-h-screen">
      <h2 className="text-2xl font-bold mb-4 px-4">Your Requests</h2>
      <div className="flex space-x-4 overflow-x-auto mb-6 w-full no-scrollbar px-4">
        {Object.entries(tabs).map(([label, value]) => (
          <button
            key={label}
            onClick={() => setStatusFilter(value)}
            className={`flex-shrink-0 px-4 py-2 rounded-full transition font-semibold max-w-full ${
              statusFilter === value
                ? "bg-accent text-black"
                : "bg-secondary hover:bg-opacity-80"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {requests.length === 0 ? (
        <p className="text-gray-500 text-center text-sm sm:text-base">
          No requests yet.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 px-0 sm:px-4">
          {requests.map((request) => (
            <div
              key={request.id}
              className="bg-gradient-to-br from-cardBg to-secondary relative py-4 px-2 sm:px-4 rounded-2xl flex flex-col justify-between h-full shadow-md"
            >
              <div className="absolute top-2 right-2 text-sm text-gray-500 flex items-center gap-1">
                {formatDate(request.createdAt)}
                <span className="text-accent text-lg ml-0.5">
                  <Clock4 className="size-[14px]" />
                </span>
              </div>

              <div className="flex items-center pt-6">
                <img
                  src={request.albumArt || "https://placehold.co/100"}
                  alt={request.trackName}
                  className="w-24 h-24 rounded-md"
                />
                <div className="flex-grow">
                  <p className="font-semibold text-base sm:text-lg pl-3">
                    {request.trackName} - {request.artist}
                  </p>
                  <p className="text-textMuted text-sm pt-2 pl-3">
                    Location: {request.place.name}
                  </p>
                  <p className="text-textMuted text-sm pl-3">
                    DJ: {request.djName}
                  </p>
                  <p className="text-textMuted text-sm pl-3">
                    Price: {request.price} PLN
                  </p>
                </div>
              </div>

              {renderRequestTracker(request.status)}

              {request.transaction && request.status === STATUS.APPROVED && (
                <div className="self-end mt-6">
                  <a
                    href={request.transaction.paymentLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-accent hover:bg-opacity-80 text-white px-4 py-2 rounded-2xl font-semibold"
                  >
                    Pay
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserRequests;
